/*
  THAANI Videoplayer (snippets/thaani-video-player.liquid)
  Eigene Steuerleiste statt der Browser-Controls. Das Video steckt bis zum
  ersten Klick im <template>, damit nichts geladen wird, was niemand sehen will.
*/

class ThaaniPlayer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bereit) return;
    this.dataset.bereit = 'true';

    this.poster = this.querySelector('.thaani-player__poster');
    this.leiste = this.querySelector('.thaani-player__leiste');
    this.knopfPlay = this.querySelector('[data-rolle="abspielen"]');
    this.knopfTon = this.querySelector('[data-rolle="ton"]');
    this.knopfVoll = this.querySelector('[data-rolle="vollbild"]');
    this.spur = this.querySelector('[data-rolle="spur"]');
    this.zeit = this.querySelector('[data-rolle="zeit"]');
    this.dauerFeld = this.querySelector('[data-rolle="dauer"]');
    this.video = null;
    this.zieht = false;

    if (this.poster) this.poster.addEventListener('click', () => this.starten());
    if (this.knopfPlay) this.knopfPlay.addEventListener('click', () => this.umschalten());
    if (this.knopfTon) this.knopfTon.addEventListener('click', () => this.tonUmschalten());
    if (this.knopfVoll) this.knopfVoll.addEventListener('click', () => this.vollbild());

    if (this.spur) {
      this.spur.addEventListener('input', () => this.springen());
      this.spur.addEventListener('pointerdown', () => { this.zieht = true; });
      ['pointerup', 'pointercancel'].forEach((e) =>
        this.spur.addEventListener(e, () => { this.zieht = false; })
      );
    }

    this.addEventListener('keydown', (e) => this.taste(e));
  }

  /* Erst beim Klick wird das Video aus dem Template geholt */
  starten() {
    if (!this.video) {
      const vorlage = this.querySelector('template');
      if (!vorlage) return;
      const knoten = vorlage.content.firstElementChild.cloneNode(true);
      this.insertBefore(knoten, this.leiste);
      this.video = this.querySelector('video');
      if (!this.video) return;

      this.video.setAttribute('playsinline', '');
      this.video.removeAttribute('controls');
      this.video.classList.add('thaani-player__video');

      this.video.addEventListener('loadedmetadata', () => this.dauerZeigen());
      this.video.addEventListener('timeupdate', () => this.fortschritt());
      this.video.addEventListener('play', () => this.zustand(true));
      this.video.addEventListener('pause', () => this.zustand(false));
      this.video.addEventListener('ended', () => this.beenden());
      this.video.addEventListener('click', () => this.umschalten());

      this.setAttribute('laeuft', '');
      if (this.leiste) this.leiste.hidden = false;
      if (this.poster) this.poster.hidden = true;
    }

    if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
    const versuch = this.video.play();
    if (versuch && versuch.catch) {
      /* Ohne erlaubten Ton lieber stumm starten als gar nicht */
      versuch.catch(() => {
        this.video.muted = true;
        this.tonIkon();
        this.video.play().catch(() => {});
      });
    }
  }

  umschalten() {
    if (!this.video) return this.starten();
    if (this.video.paused) this.video.play();
    else this.video.pause();
  }

  zustand(laeuft) {
    if (!this.knopfPlay) return;
    const play = this.knopfPlay.querySelector('.thaani-player__ikon-play');
    const pause = this.knopfPlay.querySelector('.thaani-player__ikon-pause');
    if (play) play.hidden = laeuft;
    if (pause) pause.hidden = !laeuft;
    this.knopfPlay.setAttribute('aria-label', laeuft ? 'Pause' : 'Abspielen');
    this.toggleAttribute('spielt', laeuft);
  }

  tonUmschalten() {
    if (!this.video) return;
    this.video.muted = !this.video.muted;
    this.tonIkon();
  }

  tonIkon() {
    if (!this.knopfTon || !this.video) return;
    const ton = this.knopfTon.querySelector('.thaani-player__ikon-ton');
    const stumm = this.knopfTon.querySelector('.thaani-player__ikon-stumm');
    if (ton) ton.hidden = this.video.muted;
    if (stumm) stumm.hidden = !this.video.muted;
    this.knopfTon.setAttribute('aria-label', this.video.muted ? 'Ton einschalten' : 'Ton ausschalten');
  }

  vollbild() {
    if (!this.video) return;
    const ziel = this;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (ziel.requestFullscreen) {
      ziel.requestFullscreen();
    } else if (this.video.webkitEnterFullscreen) {
      /* iPhone kennt nur Vollbild auf dem Videoelement selbst */
      this.video.webkitEnterFullscreen();
    }
  }

  springen() {
    if (!this.video || !this.video.duration) return;
    this.video.currentTime = (this.spur.value / 1000) * this.video.duration;
  }

  fortschritt() {
    if (!this.video || !this.video.duration) return;
    if (!this.zieht && this.spur) {
      const anteil = this.video.currentTime / this.video.duration;
      this.spur.value = Math.round(anteil * 1000);
      this.spur.style.setProperty('--thaani-player-anteil', (anteil * 100).toFixed(2) + '%');
    }
    if (this.zeit) this.zeit.textContent = ThaaniPlayer.zeitText(this.video.currentTime);
  }

  dauerZeigen() {
    if (this.dauerFeld && this.video) this.dauerFeld.textContent = ThaaniPlayer.zeitText(this.video.duration);
    this.tonIkon();
  }

  beenden() {
    this.zustand(false);
    if (this.video) this.video.currentTime = 0;
    if (this.spur) {
      this.spur.value = 0;
      this.spur.style.setProperty('--thaani-player-anteil', '0%');
    }
  }

  taste(e) {
    if (!this.video) return;
    const imRegler = e.target === this.spur;
    if (e.code === 'Space' || e.code === 'KeyK') {
      if (imRegler) return;
      e.preventDefault();
      this.umschalten();
    } else if (e.code === 'ArrowRight' && !imRegler) {
      e.preventDefault();
      this.video.currentTime = Math.min(this.video.duration, this.video.currentTime + 5);
    } else if (e.code === 'ArrowLeft' && !imRegler) {
      e.preventDefault();
      this.video.currentTime = Math.max(0, this.video.currentTime - 5);
    } else if (e.code === 'KeyM') {
      this.tonUmschalten();
    }
  }

  static zeitText(sekunden) {
    if (!isFinite(sekunden)) return '0:00';
    const s = Math.floor(sekunden % 60);
    const m = Math.floor(sekunden / 60);
    return m + ':' + String(s).padStart(2, '0');
  }
}

if (!customElements.get('thaani-player')) {
  customElements.define('thaani-player', ThaaniPlayer);
}
